import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { DatePicker } from '@/components/ui/date-picker'
import { UnderlineTabs } from '@/components/ui/underline-tabs'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, ArrowLeft } from 'lucide-react'
import UserAvatar from '@/components/UserAvatar'
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { toast } from '@/hooks/use-toast'
import { formatNumberSmart } from '@/lib/utils'
import NewProgramWizard from './components/NewProgramWizard'

const detailTabs = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'programs', label: 'Programs' },
  { id: 'settings', label: 'Settings' },
  { id: 'inbody', label: 'InBody' },
]

// Recharts strokes are SVG attributes (no var()); resolve the — possibly brand — primary colour once.
const trendColor = (() => {
  if (typeof document === 'undefined') return '#2563EB'
  const value = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
  return value ? `hsl(${value})` : '#2563EB'
})()

// The API stores gender as 'm' / 'f' (what createclient/updateclient use).
// Older records may hold 'male' / 'female', so normalize both ways.
const toApiGender = (value) => {
  const v = String(value || '').trim().toLowerCase()
  if (v === 'm' || v === 'male') return 'm'
  if (v === 'f' || v === 'female') return 'f'
  return ''
}
const genderLabel = (value) => ({ m: 'Male', f: 'Female' })[toApiGender(value)] || 'Not set'

// Client settings layout helpers: a titled row with its content, a label/value
// pair, and a labeled edit control.
const SettingsSection = ({ title, children }) => (
  <section className="grid gap-3 px-5 py-4 md:grid-cols-[10rem_1fr] md:gap-6">
    <h4 className="text-sm font-medium text-slate-500">{title}</h4>
    <div className="min-w-0">{children}</div>
  </section>
)

const DetailItem = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[13px] text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-sm font-medium text-slate-900">{children}</dd>
  </div>
)

const EditField = ({ label, id, className = '', children }) => (
  <div className={`space-y-1 ${className}`}>
    {id ? (
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">{label}</label>
    ) : (
      <p className="text-sm font-medium text-slate-700">{label}</p>
    )}
    {children}
  </div>
)

const ConsultantClientDetail = () => {
  const { companyUrl, startImpersonation } = useAuthStore()
  const { clientId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Supports being mounted under /consultant-clients or /company-clients
  const basePath = `/${location.pathname.split('/')[1] || 'consultant-clients'}`
  const [detailClient, setDetailClient] = useState(null)
  const [detailData, setDetailData] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [detailDate, setDetailDate] = useState(new Date())
  const [activeDetailTab, setActiveDetailTab] = useState(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab && detailTabs.some((t) => t.id === tab)) return tab
    return location.state?.activeTab || 'dashboard'
  })
  const [selectedRole, setSelectedRole] = useState('')
  const [selectedConsultantId, setSelectedConsultantId] = useState('none')
  const [initialAccountSettings, setInitialAccountSettings] = useState({ role: '', consultantId: 'none' })
  const [roleOptions, setRoleOptions] = useState([])
  const [roleOptionsLoading, setRoleOptionsLoading] = useState(false)
  const [roleOptionsLoaded, setRoleOptionsLoaded] = useState(false)
  const [consultantOptions, setConsultantOptions] = useState([])
  const [consultantOptionsLoading, setConsultantOptionsLoading] = useState(false)
  const [consultantOptionsLoaded, setConsultantOptionsLoaded] = useState(false)
  const [programWizardOpen, setProgramWizardOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [profileForm, setProfileForm] = useState(null)
  const [initialProfile, setInitialProfile] = useState(null)
  const [settingsMessage, setSettingsMessage] = useState('')
  const [settingsErrorMessage, setSettingsErrorMessage] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [editingProfile, setEditingProfile] = useState(false)
  const [editingPassword, setEditingPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [startingImpersonation, setStartingImpersonation] = useState(false)
  const [inbodyLoading, setInbodyLoading] = useState(false)
  const [inbodyError, setInbodyError] = useState('')
  const [inbodyDates, setInbodyDates] = useState([])
  const [inbodySelectedDate, setInbodySelectedDate] = useState('')
  const [inbodyMeasurement, setInbodyMeasurement] = useState(null)

  useEffect(() => {
    setInbodyLoading(false)
    setInbodyError('')
    setInbodyDates([])
    setInbodySelectedDate('')
    setInbodyMeasurement(null)
  }, [clientId, companyUrl])

  useEffect(() => {
    setRoleOptions([])
    setRoleOptionsLoaded(false)
    setRoleOptionsLoading(false)
    setConsultantOptions([])
    setConsultantOptionsLoaded(false)
    setConsultantOptionsLoading(false)
  }, [companyUrl])

  const formatName = useCallback((client) => {
    const parts = [client?.firstname, client?.middlename, client?.lastname].filter(Boolean)
    if (parts.length) return parts.join(' ')
    return client?.email || 'Unnamed Client'
  }, [])

  const formatDateParam = (date) => {
    if (!date) return null
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${year}-${month}-${day}`
  }

  const formatDisplayDate = (value) => {
    if (!value) return 'Not scheduled'
    const parsed = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(parsed.getTime())) return typeof value === 'string' ? value : 'Not scheduled'
    return parsed.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  }

  const formatInbodyDateTime = (value) => {
    const raw = String(value || '').trim()
    if (!/^\d{14}$/.test(raw)) return raw || 'Not available'
    const year = Number(raw.slice(0, 4))
    const month = Number(raw.slice(4, 6))
    const day = Number(raw.slice(6, 8))
    const hour = Number(raw.slice(8, 10))
    const minute = Number(raw.slice(10, 12))
    const second = Number(raw.slice(12, 14))
    const parsed = new Date(year, month - 1, day, hour, minute, second)
    if (Number.isNaN(parsed.getTime())) return raw
    return parsed.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const clampPercent = (value) => Math.max(0, Math.min(100, value))

  const parseDateValue = (value) => {
    if (!value) return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) return parsed
    const parts = String(value).split(/[/-]/)
    if (parts.length === 3) {
      const [partA, partB, partC] = parts
      const monthFirst = new Date(`${partA}/${partB}/${partC}`)
      if (!Number.isNaN(monthFirst.getTime())) return monthFirst
      const monthSecond = new Date(`${partB}/${partA}/${partC}`)
      if (!Number.isNaN(monthSecond.getTime())) return monthSecond
    }
    return null
  }

  const formatDateForApiValue = (value) => {
    const date = parseDateValue(value)
    if (!date) return null
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${year}-${month}-${day}`
  }

  const resolveRoleValue = (role) => role?.id != null ? String(role.id) : role?.name || role?.display_name || ''

  const formatConsultantName = useCallback((consultant) => {
    const fullName = [consultant?.firstname, consultant?.lastname].filter(Boolean).join(' ').trim()
    if (fullName) return fullName
    return consultant?.email || 'Unnamed Consultant'
  }, [])

  const findInitialConsultantValue = (clientRecord) => {
    const consultantId =
      clientRecord?.consultant_id ??
      clientRecord?.consultantId ??
      clientRecord?.consultant?.id ??
      null
    return consultantId != null ? String(consultantId) : 'none'
  }

  const extractClientRoleValue = (clientRecord) => {
    if (!clientRecord) return ''
    if (clientRecord?.role_id != null) return String(clientRecord.role_id)
    if (clientRecord?.role_users?.[0]?.role_id != null) return String(clientRecord.role_users[0].role_id)
    if (clientRecord?.roles?.[0]?.id != null) return String(clientRecord.roles[0].id)
    if (clientRecord?.role != null) return String(clientRecord.role)
    if (clientRecord?.role_name) return String(clientRecord.role_name)
    if (clientRecord?.primary_role) return String(clientRecord.primary_role)
    if (clientRecord?.primaryRole) return String(clientRecord.primaryRole)
    return ''
  }

  const findInitialRole = (roles, clientRecord) => {
    const clientRoleSeed = extractClientRoleValue(clientRecord)
    if (clientRoleSeed && roles?.length) {
      const byResolved = roles.find((role) => String(resolveRoleValue(role)) === String(clientRoleSeed))
      if (byResolved) return resolveRoleValue(byResolved)
      const byName = roles.find((role) => role.name === clientRoleSeed || role.display_name === clientRoleSeed)
      if (byName) return resolveRoleValue(byName)
    }
    if (clientRoleSeed) return String(clientRoleSeed)
    if (!roles || roles.length === 0) return ''
    const clientRoleKey = extractClientRoleValue(clientRecord)
    const byId = roles.find((role) => String(role.id) === String(clientRoleKey))
    if (byId) return resolveRoleValue(byId)
    const byName = roles.find((role) => role.name === clientRoleKey || role.display_name === clientRoleKey)
    if (byName) return resolveRoleValue(byName)
    const flagged = roles.find((role) => role.is_primary || role.is_default || role.selected || role.is_selected)
    if (flagged) return resolveRoleValue(flagged)
    const clientRole = roles.find((role) => (role.name || '').toLowerCase() === 'client' || (role.display_name || '').toLowerCase() === 'client')
    if (clientRole) return resolveRoleValue(clientRole)
    return resolveRoleValue(roles[0])
  }

  const fetchRoleOptions = useCallback(async () => {
    if (!companyUrl || roleOptionsLoaded || roleOptionsLoading) return
    setRoleOptionsLoading(true)
    setSettingsErrorMessage('')
    const result = await apiService.get(`/${companyUrl}/permission/roles`)
    if (result.success) {
      const fetchedRoles = result.data || []
      setRoleOptions(fetchedRoles)
      setRoleOptionsLoaded(true)
      setSelectedRole((current) => {
        if (!current) return findInitialRole(fetchedRoles, detailData?.clientRecord)
        const matched = fetchedRoles.find((role) => {
          const resolved = resolveRoleValue(role)
          return resolved === current || role?.name === current || role?.display_name === current
        })
        return matched ? resolveRoleValue(matched) : current
      })
    } else {
      setSettingsErrorMessage(result.error || 'Failed to load roles')
    }
    setRoleOptionsLoading(false)
  }, [companyUrl, roleOptionsLoaded, roleOptionsLoading, detailData])

  const fetchConsultantOptions = useCallback(async () => {
    if (!companyUrl || consultantOptionsLoaded || consultantOptionsLoading) return
    setConsultantOptionsLoading(true)
    setSettingsErrorMessage('')
    const payload = {
      fromIndex: 0,
      limit: 500,
      orderBy: 'DESC',
      orderByOn: 'id',
      searchtext: '',
    }
    const result = await apiService.post(`/${companyUrl}/companyadmin/getconsultants`, payload)
    if (result.success) {
      setConsultantOptions(result.data?.rows || [])
      setConsultantOptionsLoaded(true)
    } else {
      setSettingsErrorMessage(result.error || 'Failed to load consultants')
    }
    setConsultantOptionsLoading(false)
  }, [companyUrl, consultantOptionsLoaded, consultantOptionsLoading])

  useEffect(() => {
    if (!companyUrl || !clientId) return
    const controller = new AbortController()
    const loadDetail = async () => {
      setDetailLoading(true)
      setDetailError(null)
      try {
        const logdate = formatDateParam(detailDate)
        const programsPayload = { client_id: clientId, logdate }
        const dashboardPayload = { user_id: clientId, logdate }
        const [statesRes, programsRes, clientRes, dashboardRes] = await Promise.all([
          apiService.get(`/${companyUrl}/getstates`, {}, controller.signal),
          apiService.post(`/${companyUrl}/getclientprogramswithgoals`, programsPayload, controller.signal),
          apiService.post(`/${companyUrl}/getclients`, { user_id: clientId }, controller.signal),
          apiService.post(`/${companyUrl}/clientdashboard`, dashboardPayload, controller.signal),
        ])

        const clientRecord = clientRes.success ? clientRes.data?.rows?.[0] || clientRes.data?.[0] || clientRes.data : null
        const programsWithGoals = programsRes.success ? programsRes.data : null
        const fetchedStates = statesRes.success ? statesRes.data || [] : []
        const dashboard = dashboardRes.success ? dashboardRes.data : null

        setDetailClient(clientRecord || { id: clientId })
        setDetailData({
          programsWithGoals,
          clientRecord,
          states: fetchedStates,
          dashboard,
        })
        setSelectedRole(findInitialRole([], clientRecord))
        setSelectedConsultantId(findInitialConsultantValue(clientRecord))
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setDetailError('Failed to load client details')
      } finally {
        setDetailLoading(false)
      }
    }

    loadDetail()
    return () => controller.abort()
  }, [companyUrl, clientId, detailDate, reloadKey])

  const detailViewData = useMemo(() => {
    if (!detailClient) return null

    const userFromPrograms = detailData?.programsWithGoals?.user
    const userFromClients = detailData?.clientRecord
    const user = userFromClients || userFromPrograms || detailClient
    const dashboard = detailData?.dashboard

    const name = formatName(user)
    const email = user?.email || 'No email provided'
    const companyName = user?.company?.name || detailClient?.company?.name || 'Company not set'
    const initials =
      name
        ?.split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'CL'

    const programList = detailData?.programsWithGoals?.programswithgoals || []
    const activeProgramId = detailData?.programsWithGoals?.activeprogram_id
    const primaryGoal = dashboard?.goal || programList.find((p) => String(p.id) === String(activeProgramId)) || programList[0]
    const goalSessions = primaryGoal?.packagepurchased || primaryGoal?.programs?.length || 0
    const sessionsCompleted = primaryGoal?.programs?.length || 0
    const nextSession = formatDisplayDate(primaryGoal?.start_date)

    const latestWeight = dashboard?.weights?.[0]
    const weightEntries = Array.isArray(dashboard?.weights) ? dashboard.weights : []
    const trendData = (() => {
      const normalized = weightEntries
        .map((entry) => {
          const rawDate = entry?.measured_at || entry?.logdate || entry?.date || entry?.created_at || entry?.timestamp
          const parsedDate = rawDate ? new Date(rawDate) : null
          const usableDate = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null
          const label = usableDate
            ? usableDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
            : rawDate || 'Entry'
          return {
            date: label,
            ts: usableDate ? usableDate.getTime() : 0,
            weight: entry?.weight != null ? Number(entry.weight) : null,
            bodyFat: entry?.bodyfat != null ? Number(entry.bodyfat) : null,
            muscleMass: entry?.leanmuscle != null ? Number(entry.leanmuscle) : null,
          }
        })
        .filter((entry) => entry.weight !== null || entry.bodyFat !== null || entry.muscleMass !== null)
        .sort((a, b) => a.ts - b.ts)

      return normalized.map(({ ts, ...rest }) => rest)
    })()

    const rangeLabel =
      trendData.length > 1 ? `${trendData[0].date} - ${trendData[trendData.length - 1].date}` : trendData[0]?.date || 'No data'

    const latestEntry = trendData[trendData.length - 1]
    const comparisonEntry = trendData.length > 1 ? trendData[trendData.length - 2] : trendData[0]
    const deltaWeight =
      comparisonEntry?.weight != null && latestEntry?.weight != null ? latestEntry.weight - comparisonEntry.weight : null
    const deltaBodyFat =
      comparisonEntry?.bodyFat != null && latestEntry?.bodyFat != null ? latestEntry.bodyFat - comparisonEntry.bodyFat : null
    const deltaMuscle =
      comparisonEntry?.muscleMass != null && latestEntry?.muscleMass != null ? latestEntry.muscleMass - comparisonEntry.muscleMass : null

    const formatDelta = (value, unit) => {
      if (value == null) return 'No change recorded'
      const sign = value > 0 ? '+' : ''
      return `${sign}${formatNumberSmart(value, 2)} ${unit} since start`
    }

    const composition = [
      {
        label: 'Weight',
        value: latestEntry?.weight != null ? `${formatNumberSmart(latestEntry.weight, 2)} lbs` : latestWeight?.weight ? `${formatNumberSmart(latestWeight.weight, 2)} lbs` : '—',
        delta: formatDelta(deltaWeight, 'lbs'),
      },
      {
        label: 'Body Fat',
        value: latestEntry?.bodyFat != null ? `${formatNumberSmart(latestEntry.bodyFat, 2)}%` : latestWeight?.bodyfat ? `${formatNumberSmart(latestWeight.bodyfat, 2)}%` : '—',
        delta: formatDelta(deltaBodyFat, '%'),
      },
      {
        label: 'Muscle Mass',
        value: latestEntry?.muscleMass != null ? `${formatNumberSmart(latestEntry.muscleMass, 2)} lbs` : latestWeight?.leanmuscle ? `${formatNumberSmart(latestWeight.leanmuscle, 2)} lbs` : '—',
        delta: formatDelta(deltaMuscle, 'lbs'),
      },
    ]

    const nutritionData = dashboard?.nutrition
      ? {
          protein: {
            current: dashboard.nutrition.protein?.consumed ?? 0,
            goal: dashboard.nutrition.protein?.total ?? 0,
          },
          fat: {
            current: dashboard.nutrition.fat?.consumed ?? 0,
            goal: dashboard.nutrition.fat?.total ?? 0,
          },
          carbs: {
            current: dashboard.nutrition.carbs?.consumed ?? 0,
            goal: dashboard.nutrition.carbs?.total ?? 0,
          },
        }
      : null

    const caloriesData = dashboard?.calories
      ? {
          current: dashboard.calories.consumed ?? 0,
          goal: dashboard.calories.total ?? 0,
        }
      : null

    const programs = {
      active: primaryGoal
        ? [
            {
              id: primaryGoal.id,
              name: primaryGoal.name || 'Active Program',
              sessions: `${primaryGoal.packagepurchased || primaryGoal.programs?.length || 0} Sessions`,
              dates:
                primaryGoal.start_date && primaryGoal.estimated_end_date
                  ? `${primaryGoal.start_date?.slice(0, 10)} - ${primaryGoal.estimated_end_date?.slice(0, 10)}`
                  : 'Dates not available',
              status: 'Active',
            },
          ]
        : [],
      past: programList
        .filter((program) => !primaryGoal || program.id !== primaryGoal.id)
        .map((program) => ({
          id: program.id,
          name: program.name || 'Past Program',
          sessions: `${program.packagepurchased || program.programs?.length || 0} Sessions`,
          dates:
            program.start_date && program.estimated_end_date
              ? `${program.start_date?.slice(0, 10)} - ${program.estimated_end_date?.slice(0, 10)}`
              : 'Dates not available',
          status: 'Completed',
        })),
    }

    const profile = {
      name,
      email,
      address:
        userFromClients?.personal?.address ||
        userFromPrograms?.address ||
        user?.address ||
        'Address not available',
      gender: genderLabel(userFromClients?.personal?.gender || user?.gender),
      birthdate: userFromClients?.personal?.birthdate || user?.birthdate || 'Not available',
      mobile: userFromClients?.personal?.mobilephone || user?.mobilephone || user?.mobile || 'Not provided',
      state: userFromClients?.personal?.state,
      city: userFromClients?.personal?.city,
      postalcode: userFromClients?.personal?.postalcode,
    }

    const account = {
      password: '••••••••',
      company: companyName,
      consultant:
        user?.consultant
          ? formatConsultantName(user.consultant)
          : detailClient?.consultant
            ? formatConsultantName(detailClient.consultant)
            : 'Not assigned',
    }

    return {
      name,
      email,
      companyName,
      initials,
      avatarSeed: name || email || `client-${detailClient.id}`,
      goalProgress: { completed: sessionsCompleted, total: goalSessions, next: nextSession },
      trends: { data: trendData, rangeLabel },
      composition,
      nutrition: nutritionData,
      calories: caloriesData,
      exercise: dashboard?.exercise || detailData?.exercise || null,
      programs,
      profile,
      account,
    }
  }, [detailClient, detailData, formatName, formatConsultantName])

  useEffect(() => {
    if (!detailViewData) return
    const client = detailData?.clientRecord || detailData?.programsWithGoals?.user || detailClient || {}
    const personal = client.personal || {}
    const snapshot = {
      firstname: client.firstname || '',
      middlename: client.middlename || '',
      lastname: client.lastname || '',
      email: client.email || '',
      gender: toApiGender(personal.gender || client.gender),
      birthdate: personal.birthdate || client.birthdate || '',
      mobile: personal.mobilephone || client.mobilephone || client.mobile || '',
      address: personal.address || client.address || '',
      aptsuite: personal.aptsuite || '',
      city: personal.city || client.city || '',
      state: personal.state || client.state || '',
      postalcode: personal.postalcode || client.postalcode || '',
    }
    setProfileForm(snapshot)
    setInitialProfile(snapshot)
    setSettingsMessage('')
    setSettingsErrorMessage('')
    const initialRoleValue = findInitialRole([], client)
    const initialConsultantValue = findInitialConsultantValue(client)
    setSelectedRole(initialRoleValue)
    setSelectedConsultantId(initialConsultantValue)
    setInitialAccountSettings({ role: initialRoleValue, consultantId: initialConsultantValue })
  }, [detailViewData, detailData, detailClient])

  const rolesList = roleOptions
  const selectedRoleMeta = useMemo(
    () => rolesList.find((role) => resolveRoleValue(role) === selectedRole),
    [rolesList, selectedRole]
  )
  const selectedConsultantMeta = useMemo(
    () => consultantOptions.find((consultant) => String(consultant.id) === String(selectedConsultantId)),
    [consultantOptions, selectedConsultantId]
  )
  const selectedRoleLabel = useMemo(() => {
    if (selectedRoleMeta) return selectedRoleMeta.display_name || selectedRoleMeta.name || selectedRole
    const roleFromClient = (detailData?.clientRecord?.roles || []).find((role) => String(role.id) === String(selectedRole))
    if (roleFromClient) return roleFromClient.display_name || roleFromClient.name || selectedRole
    return selectedRole || 'Not set'
  }, [selectedRoleMeta, detailData, selectedRole])
  const selectedConsultantLabel = useMemo(() => {
    if (selectedConsultantId === 'none') return 'Not assigned'
    if (selectedConsultantMeta) return formatConsultantName(selectedConsultantMeta)
    if (detailViewData?.account?.consultant) return detailViewData.account.consultant
    return 'Not assigned'
  }, [selectedConsultantId, selectedConsultantMeta, detailViewData])

  const updateProfileField = (field, value) => {
    setProfileForm((prev) => ({ ...(prev || {}), [field]: value }))
    setSettingsMessage('')
    setSettingsErrorMessage('')
  }

  const resetProfileForm = () => {
    if (initialProfile) {
      setProfileForm(initialProfile)
      setSelectedRole(initialAccountSettings.role || '')
      setSelectedConsultantId(initialAccountSettings.consultantId || 'none')
      setSettingsMessage('')
      setSettingsErrorMessage('')
    }
  }

  const handleSaveProfile = async () => {
    if (!profileForm || !companyUrl) return
    const userId = detailClient?.id || detailData?.clientRecord?.id || clientId
    if (!userId) {
      setSettingsErrorMessage('Missing client id for update')
      return
    }
    setSavingProfile(true)
    setSettingsMessage('')
    setSettingsErrorMessage('')
    const payload = {
      user_id: userId,
      firstname: profileForm.firstname?.trim(),
      middlename: profileForm.middlename?.trim() || null,
      lastname: profileForm.lastname?.trim(),
      email: profileForm.email?.trim(),
      mobilephone: profileForm.mobile?.trim(),
      gender: profileForm.gender || null,
      birthdate: formatDateForApiValue(profileForm.birthdate),
      address: profileForm.address?.trim(),
      aptsuite: profileForm.aptsuite?.trim(),
      city: profileForm.city?.trim(),
      state: profileForm.state || null,
      postalcode: profileForm.postalcode?.trim(),
      role: selectedRole || null,
      consultant_id: selectedConsultantId && selectedConsultantId !== 'none' ? Number(selectedConsultantId) : null,
    }
    const sanitized = Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined))
    const result = await apiService.updateClient(companyUrl, sanitized)
    if (result.success) {
      setSettingsMessage('Profile updated successfully')
      setInitialProfile(profileForm)
      setInitialAccountSettings({
        role: selectedRole || '',
        consultantId: selectedConsultantId || 'none',
      })
      setEditingProfile(false)
      setReloadKey((k) => k + 1)
    } else {
      setSettingsErrorMessage(result.error || 'Failed to update client')
    }
    setSavingProfile(false)
  }

  const handleUpdatePassword = async () => {
    const userId = detailClient?.id || detailData?.clientRecord?.id || clientId
    if (!userId || !companyUrl) {
      setSettingsErrorMessage('Missing client context for password update')
      return
    }
    if (!newPassword.trim()) {
      setSettingsErrorMessage('Please enter a new password')
      return
    }
    setSavingPassword(true)
    setSettingsMessage('')
    setSettingsErrorMessage('')
    try {
      const result = await apiService.put(`/${companyUrl}/updateclient`, {
        user_id: Number(userId),
        password: newPassword.trim(),
      })
      if (result.success) {
        setSettingsMessage('Password updated successfully')
        setEditingPassword(false)
        setNewPassword('')
        setReloadKey((k) => k + 1)
      } else {
        setSettingsErrorMessage(result.error || 'Failed to update password')
      }
    } catch (err) {
      setSettingsErrorMessage('An error occurred while updating password')
    } finally {
      setSavingPassword(false)
    }
  }

  const handleDeleteAccount = () => {
    setSettingsMessage('')
    setSettingsErrorMessage('')
    setDeleteModalOpen(true)
  }

  const confirmDeleteAccount = async () => {
    const userId = detailClient?.id || detailData?.clientRecord?.id || clientId
    if (!companyUrl || !userId) {
      setSettingsErrorMessage('Missing client context for deletion')
      setDeleteModalOpen(false)
      return
    }
    setDeletingAccount(true)
    setSettingsMessage('')
    setSettingsErrorMessage('')
    const result = await apiService.deleteClient(companyUrl, userId)
    if (result.success) {
      setSettingsMessage('Account deleted')
      setDeleteModalOpen(false)
      navigate(basePath)
    } else {
      setSettingsErrorMessage(result.error || 'Failed to delete client')
    }
    setDeletingAccount(false)
  }

  const handleActAsUser = async () => {
    const userId = detailClient?.id || detailData?.clientRecord?.id || clientId
    if (!companyUrl || !userId) {
      setSettingsErrorMessage('Missing client context for impersonation')
      return
    }

    setSettingsMessage('')
    setSettingsErrorMessage('')
    setStartingImpersonation(true)

    const result = await startImpersonation(userId, `${basePath}/${clientId}?tab=settings`)
    if (result.success) {
      setSettingsMessage('Impersonation started')
      navigate('/dashboard')
    } else {
      // Starting a session swaps the app for the loading screen, which unmounts this page and
      // drops local state, so the failure is reported with a toast that outlives it.
      toast({
        title: 'Unable to act as user',
        description: result.error || 'Failed to act as user',
        variant: 'destructive'
      })
    }

    setStartingImpersonation(false)
  }

  const resolveInbodyUserToken = useCallback(() => {
    const client = detailData?.clientRecord || detailData?.programsWithGoals?.user || detailClient || {}
    const personal = client?.personal || {}
    return (
      personal?.mobilephone ||
      client?.mobilephone ||
      client?.mobile ||
      client?.phone ||
      client?.usertoken ||
      client?.user_token ||
      client?.token ||
      ''
    )
  }, [detailData, detailClient])

  const fetchInbodyData = useCallback(async () => {
    if (!companyUrl || inbodyLoading) return

    const usertoken = String(resolveInbodyUserToken() || '').trim()
    if (!usertoken) {
      setInbodyError('Unable to fetch InBody data because user token is missing.')
      setInbodyDates([])
      setInbodySelectedDate('')
      setInbodyMeasurement(null)
      return
    }

    setInbodyLoading(true)
    setInbodyError('')

    try {
      const datesRes = await apiService.post(`/${companyUrl}/inbody/getclientmeasuredondates`, { usertoken })
      if (!datesRes.success) {
        setInbodyError(datesRes.error || 'Failed to load measured dates.')
        setInbodyDates([])
        setInbodySelectedDate('')
        setInbodyMeasurement(null)
        return
      }

      const measuredDates = Array.isArray(datesRes.data?.measuredDates) ? datesRes.data.measuredDates : []
      setInbodyDates(measuredDates)
      const firstDate = measuredDates[0] || ''
      setInbodySelectedDate(firstDate)

      if (!firstDate) {
        setInbodyMeasurement(null)
        return
      }

      let measurementRes = await apiService.post(`/${companyUrl}/inbody/getclientmeasurements`, { usertoken, datetimes: firstDate })
      if (!measurementRes.success) {
        measurementRes = await apiService.post(`/${companyUrl}/inbody/getclientmeasurements`, { usertoken })
      }
      if (!measurementRes.success) {
        setInbodyError(measurementRes.error || 'Failed to load InBody measurements.')
        setInbodyMeasurement(null)
        return
      }

      setInbodyMeasurement(measurementRes.data || null)
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
      setInbodyError('Failed to load InBody measurements.')
      setInbodyMeasurement(null)
    } finally {
      setInbodyLoading(false)
    }
  }, [companyUrl, inbodyLoading, resolveInbodyUserToken])

  const handleInbodyDateSelect = useCallback(async (dateTime) => {
    if (!companyUrl || inbodyLoading || !dateTime || dateTime === inbodySelectedDate) return
    const usertoken = String(resolveInbodyUserToken() || '').trim()
    if (!usertoken) return

    setInbodyLoading(true)
    setInbodyError('')
    setInbodySelectedDate(dateTime)
    try {
      let measurementRes = await apiService.post(`/${companyUrl}/inbody/getclientmeasurements`, { usertoken, datetimes: dateTime })
      if (!measurementRes.success) {
        measurementRes = await apiService.post(`/${companyUrl}/inbody/getclientmeasurements`, { usertoken })
      }
      if (!measurementRes.success) {
        setInbodyError(measurementRes.error || 'Failed to load InBody measurements.')
        setInbodyMeasurement(null)
        return
      }
      setInbodyMeasurement(measurementRes.data || null)
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
      setInbodyError('Failed to load InBody measurements.')
      setInbodyMeasurement(null)
    } finally {
      setInbodyLoading(false)
    }
  }, [companyUrl, inbodyLoading, inbodySelectedDate, resolveInbodyUserToken])

  const handleDetailTabChange = useCallback((tabId) => {
    setActiveDetailTab(tabId)
    if (tabId === 'inbody') {
      fetchInbodyData()
    }
  }, [fetchInbodyData])

  return (
    <div className="app-page flex min-h-screen flex-col">
      <div>
        <button
          type="button"
          onClick={() => navigate(basePath)}
          className="mb-2 inline-flex items-center gap-1.5 text-[13px] text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All clients
        </button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3.5">
            <UserAvatar
              name={detailViewData?.name}
              email={detailViewData?.email}
              className="h-14 w-14 flex-shrink-0 text-lg"
            />
            <div className="min-w-0">
              <h1 className="truncate text-[1.625rem] font-semibold leading-tight tracking-[-0.025em] text-slate-900">{detailViewData?.name}</h1>
              <p className="truncate text-sm text-slate-500">
                {[detailViewData?.email, detailViewData?.account?.company].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
          <Button onClick={() => setProgramWizardOpen(true)}>Create new program</Button>
        </div>
      </div>

      <UnderlineTabs
        tabs={detailTabs}
        value={activeDetailTab}
        onChange={handleDetailTabChange}
        label="Client sections"
        actions={activeDetailTab === 'dashboard' ? <DatePicker value={detailDate} onChange={(date) => setDetailDate(date)} className="h-9 w-[168px] justify-center" /> : null}
      />

      <div className="flex-1 space-y-5 pb-6">
        {detailError && (
          <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {detailError}
          </div>
        )}
        {detailLoading && (
          <div className="flex items-center space-x-3 text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading client details…</span>
          </div>
        )}
        {!detailLoading && !detailError && !detailViewData && (
          <div className="text-slate-600">Client details not available.</div>
        )}

        <AnimatePresence mode="wait">
          {activeDetailTab === 'dashboard' && detailViewData && !detailLoading && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="space-y-6"
            >

              {/* Program goals & progress */}
              <section aria-labelledby="goals-heading" className="space-y-3">
                <h2 id="goals-heading" className="text-lg font-semibold tracking-[-0.01em] text-slate-900">Program goals &amp; progress</h2>
                {detailViewData.trends.data.length ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {[
                      { key: 'weight', label: 'Weight', value: detailViewData.composition[0]?.value, delta: detailViewData.composition[0]?.delta },
                      { key: 'bodyFat', label: 'Body fat', value: detailViewData.composition[1]?.value, delta: detailViewData.composition[1]?.delta },
                      { key: 'muscleMass', label: 'Muscle mass', value: detailViewData.composition[2]?.value, delta: detailViewData.composition[2]?.delta },
                    ].map((chart) => {
                      const rows = detailViewData.trends.data
                      return (
                        <Card key={chart.key} className="p-4">
                          <p className="text-sm text-slate-500">{chart.label}</p>
                          <p className="mt-1 text-[1.75rem] font-semibold leading-tight tracking-[-0.02em] text-slate-900">{chart.value}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{chart.delta}</p>
                          <div className="mt-4 h-[80px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={rows} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                                <YAxis hide domain={['dataMin', 'dataMax']} />
                                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: 'none', fontSize: 12 }} labelStyle={{ color: '#64748B' }} />
                                <Line type="monotone" dataKey={chart.key} stroke={trendColor} strokeWidth={2} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="mt-2 flex justify-between text-[11px] text-slate-500">
                            <span>{rows[0]?.date}</span>
                            <span>{rows.length > 1 ? rows[rows.length - 1]?.date : ''}</span>
                          </div>
                        </Card>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No trend data available yet.</p>
                )}
              </section>

              {/* Daily performance */}
              <section aria-labelledby="daily-heading" className="space-y-3 border-t border-slate-200 pt-6">
                <h2 id="daily-heading" className="text-lg font-semibold tracking-[-0.01em] text-slate-900">Daily performance</h2>

                {detailViewData.nutrition ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {Object.entries(detailViewData.nutrition).map(([macro, value]) => {
                      const current = Number(value.current) || 0
                      const goal = Number(value.goal) || 0
                      const percent = Math.min(100, goal > 0 ? Math.round((current / goal) * 100) : 0)
                      return (
                        <Card key={macro} className="p-4">
                          <div className="flex items-baseline justify-between">
                            <p className="text-sm capitalize text-slate-500">{macro}</p>
                            <span className="text-xs text-slate-500">{formatNumberSmart(goal, 2)}g goal</span>
                          </div>
                          <p className="mt-1 text-[1.75rem] font-semibold leading-tight tracking-[-0.02em] text-slate-900">{formatNumberSmart(current, 2)}g</p>
                          <Progress value={percent} className="mt-3" aria-label={`${macro} progress`} />
                          <p className="mt-2 text-xs text-slate-500">{percent}% of target</p>
                        </Card>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No nutrition data available for this client.</p>
                )}

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                  <Card className="p-4">
                    <h3 className="text-[15px] font-semibold text-slate-900">Calories</h3>
                    {(() => {
                      const current = Number(detailViewData.calories?.current) || 0
                      const goal = Number(detailViewData.calories?.goal) || 0
                      return (
                        <>
                          <p className="mt-3 text-slate-900">
                            <span className="text-[1.75rem] font-semibold leading-tight tracking-[-0.02em]">{current.toLocaleString()}</span>
                            <span className="ml-1.5 text-sm text-slate-500">/ {goal.toLocaleString()} kcal</span>
                          </p>
                          <Progress value={goal > 0 ? (current / goal) * 100 : 0} className="mt-3" aria-label="Calories progress" />
                          <p className="mt-2 text-xs text-slate-500">
                            {detailViewData.calories ? `${Math.max(goal - current, 0).toLocaleString()} kcal remaining today` : 'No entries yet'}
                          </p>
                        </>
                      )
                    })()}
                  </Card>

                  <Card className="p-4">
                    <h3 className="text-[15px] font-semibold text-slate-900">Exercise progress</h3>
                    <div className="mt-3 space-y-3">
                      {detailViewData.exercise ? (
                        [
                          { label: 'Exercises', value: detailViewData.exercise.exercises },
                          { label: 'Total sets', value: detailViewData.exercise.sets },
                          { label: 'Total reps', value: detailViewData.exercise.reps },
                          { label: 'Cardio', value: detailViewData.exercise.cardio },
                        ]
                          .filter((item) => item.value)
                          .map((item) => {
                            const current = item.value.consumed ?? item.value.current ?? 0
                            const goal = item.value.total ?? item.value.goal ?? 0
                            const unit = item.value.unit || ''
                            const percent = Math.min(100, goal ? (current / goal) * 100 : 0)
                            const display = `${current}${unit ? ` ${unit}` : ''} / ${goal}${unit ? ` ${unit}` : ''}`
                            return (
                              <div key={item.label}>
                                <div className="mb-1.5 flex items-baseline justify-between">
                                  <p className="text-sm text-slate-700">{item.label}</p>
                                  <span className="text-sm font-medium text-slate-900">{display}</span>
                                </div>
                                <Progress value={percent} aria-label={`${item.label} progress`} />
                              </div>
                            )
                          })
                      ) : (
                        <p className="text-sm text-slate-500">No exercise data available.</p>
                      )}
                    </div>
                  </Card>
                </div>
              </section>
            </motion.div>
          )}

          {activeDetailTab === 'programs' && detailViewData && !detailLoading && (
            <motion.div
              key="programs"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="space-y-6"
            >
              <h2 className="text-lg font-semibold tracking-[-0.01em] text-slate-900">Active programs</h2>
              <Card className="overflow-hidden">
                <CardContent className="divide-y divide-slate-100 p-0">
                  {detailViewData.programs.active.length === 0 && (
                    <div className="px-4 py-4 text-sm text-slate-500">No active programs found.</div>
                  )}
                  {detailViewData.programs.active.map((program) => (
                    <button
                      type="button"
                      key={program.id || program.name}
                      onClick={() => {
                        if (program.id) {
                          navigate(`${basePath}/${clientId}/programs/${program.id}`)
                        }
                      }}
                      className="grid w-full grid-cols-2 items-center gap-x-4 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-background focus:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-primary/25 md:grid-cols-4"
                    >
                      <p className="text-sm font-semibold text-slate-900">{program.name}</p>
                      <p className="text-sm text-slate-600">{program.sessions}</p>
                      <p className="text-sm text-slate-600">{program.dates}</p>
                      <Badge className="w-fit border-transparent bg-emerald-50 text-emerald-700">{program.status}</Badge>
                    </button>
                  ))}
                </CardContent>
              </Card>
              <h2 className="text-lg font-semibold tracking-[-0.01em] text-slate-900">Past programs</h2>
              <Card className="overflow-hidden">
                <CardContent className="divide-y divide-slate-100 p-0">
                  {detailViewData.programs.past.length === 0 && (
                    <div className="px-4 py-4 text-sm text-slate-500">No past programs found.</div>
                  )}
                  {detailViewData.programs.past.map((program) => (
                    <button
                      type="button"
                      key={program.id || program.name}
                      onClick={() => {
                        if (program.id) {
                          navigate(`${basePath}/${clientId}/programs/${program.id}`)
                        }
                      }}
                      className="grid w-full grid-cols-2 items-center gap-x-4 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-background focus:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-primary/25 md:grid-cols-4"
                    >
                      <p className="text-sm font-semibold text-slate-900">{program.name}</p>
                      <p className="text-sm text-slate-600">{program.sessions}</p>
                      <p className="text-sm text-slate-600">{program.dates}</p>
                      <Badge className="w-fit border-transparent bg-slate-100 text-slate-600">{program.status}</Badge>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeDetailTab === 'settings' && detailViewData && !detailLoading && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <div className="space-y-5">
                {/* Client details: one Edit / Save covers profile, address and coaching. */}
                <Card>
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Client details</h3>
                      <p className="mt-0.5 text-sm text-slate-500">Profile, address and who coaches them.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {settingsErrorMessage && <span role="alert" className="text-sm text-red-600">{settingsErrorMessage}</span>}
                      {settingsMessage && <span role="status" className="text-sm text-emerald-700">{settingsMessage}</span>}
                      {!editingProfile && (
                        <Button variant="outline" size="sm" onClick={() => { setEditingProfile(true); setSettingsMessage(''); setSettingsErrorMessage('') }} disabled={savingProfile}>
                          Edit
                        </Button>
                      )}
                      {editingProfile && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => { resetProfileForm(); setEditingProfile(false) }} disabled={!profileForm || savingProfile}>Cancel</Button>
                          <Button size="sm" onClick={handleSaveProfile} disabled={!profileForm || savingProfile} aria-busy={savingProfile}>
                            {savingProfile ? 'Saving…' : 'Save changes'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="divide-y divide-slate-200">
                    <SettingsSection title="Personal">
                      {editingProfile ? (
                        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-3">
                          <EditField label="First name" id="settings-firstname">
                            <Input id="settings-firstname" value={profileForm?.firstname || ''} onChange={(e) => updateProfileField('firstname', e.target.value)} />
                          </EditField>
                          <EditField label="Middle name" id="settings-middlename">
                            <Input id="settings-middlename" value={profileForm?.middlename || ''} onChange={(e) => updateProfileField('middlename', e.target.value)} />
                          </EditField>
                          <EditField label="Last name" id="settings-lastname">
                            <Input id="settings-lastname" value={profileForm?.lastname || ''} onChange={(e) => updateProfileField('lastname', e.target.value)} />
                          </EditField>
                          <EditField label="Email" id="settings-email" className="sm:col-span-2">
                            <Input id="settings-email" type="email" value={profileForm?.email || ''} onChange={(e) => updateProfileField('email', e.target.value)} />
                          </EditField>
                          <EditField label="Mobile phone" id="settings-mobile">
                            <Input id="settings-mobile" type="tel" value={profileForm?.mobile || ''} onChange={(e) => updateProfileField('mobile', e.target.value)} />
                          </EditField>
                          <EditField label="Birth date">
                            <DatePicker value={parseDateValue(profileForm?.birthdate)} onChange={(date) => updateProfileField('birthdate', date)} />
                          </EditField>
                          <EditField label="Gender">
                            <Select value={profileForm?.gender || ''} onValueChange={(val) => updateProfileField('gender', val)}>
                              <SelectTrigger className="bg-white">
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="m">Male</SelectItem>
                                <SelectItem value="f">Female</SelectItem>
                              </SelectContent>
                            </Select>
                          </EditField>
                        </div>
                      ) : (
                        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                          <DetailItem label="Name">{detailViewData.profile.name}</DetailItem>
                          <DetailItem label="Email">{detailViewData.profile.email}</DetailItem>
                          <DetailItem label="Mobile phone">{detailViewData.profile.mobile}</DetailItem>
                          <DetailItem label="Gender">{detailViewData.profile.gender}</DetailItem>
                          <DetailItem label="Birth date">{detailViewData.profile.birthdate}</DetailItem>
                        </dl>
                      )}
                    </SettingsSection>

                    <SettingsSection title="Address">
                      {editingProfile ? (
                        <div className="space-y-3">
                          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-[2fr_1fr]">
                            <EditField label="Street address" id="settings-address">
                              <Input id="settings-address" value={profileForm?.address || ''} onChange={(e) => updateProfileField('address', e.target.value)} />
                            </EditField>
                            <EditField label="Apt or suite" id="settings-aptsuite">
                              <Input id="settings-aptsuite" value={profileForm?.aptsuite || ''} onChange={(e) => updateProfileField('aptsuite', e.target.value)} />
                            </EditField>
                          </div>
                          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-[2fr_1.4fr_1fr]">
                            <EditField label="City" id="settings-city">
                              <Input id="settings-city" value={profileForm?.city || ''} onChange={(e) => updateProfileField('city', e.target.value)} />
                            </EditField>
                            <EditField label="State">
                              <Select value={profileForm?.state || ''} onValueChange={(val) => updateProfileField('state', val)}>
                                <SelectTrigger className="bg-white">
                                  <SelectValue placeholder="Select state" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(detailData?.states || []).map((state) => (
                                    <SelectItem key={state.stateCode || state.stateName} value={state.stateCode || state.stateName}>
                                      {state.stateName || state.stateCode}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </EditField>
                            <EditField label="ZIP code" id="settings-postalcode">
                              <Input id="settings-postalcode" value={profileForm?.postalcode || ''} onChange={(e) => updateProfileField('postalcode', e.target.value)} />
                            </EditField>
                          </div>
                        </div>
                      ) : (
                        (() => {
                          const p = detailViewData.profile
                          const street = [p.address !== 'Address not available' ? p.address : '', profileForm?.aptsuite].filter(Boolean).join(', ')
                          const stateName = (detailData?.states || []).find((st) => st.stateCode === p.state)?.stateName || p.state
                          const cityLine = [[p.city, stateName].filter(Boolean).join(', '), p.postalcode].filter(Boolean).join(' ')
                          return street || cityLine ? (
                            <address className="text-sm not-italic leading-6 text-slate-900">
                              {street && <span className="block">{street}</span>}
                              {cityLine && <span className="block">{cityLine}</span>}
                            </address>
                          ) : (
                            <p className="text-sm text-slate-500">No address on file.</p>
                          )
                        })()
                      )}
                    </SettingsSection>

                    <SettingsSection title="Coaching">
                      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
                        <DetailItem label="Company">{detailViewData.account.company}</DetailItem>
                        <DetailItem label="Consultant">
                          {editingProfile ? (
                            <Select
                              value={selectedConsultantId || 'none'}
                              onOpenChange={(open) => {
                                if (open) fetchConsultantOptions()
                              }}
                              onValueChange={(val) => {
                                setSelectedConsultantId(val)
                                setSettingsMessage('')
                                setSettingsErrorMessage('')
                              }}
                              disabled={savingProfile}
                            >
                              <SelectTrigger className="mt-1 bg-white" aria-label="Consultant">
                                <SelectValue placeholder={consultantOptionsLoading ? 'Loading consultants...' : 'Choose consultant'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Not assigned</SelectItem>
                                {selectedConsultantId !== 'none' && !consultantOptions.some((consultant) => String(consultant.id) === String(selectedConsultantId)) && (
                                  <SelectItem value={String(selectedConsultantId)}>{selectedConsultantLabel}</SelectItem>
                                )}
                                {consultantOptions.map((consultant) => (
                                  <SelectItem key={consultant.id} value={String(consultant.id)}>
                                    {formatConsultantName(consultant)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            selectedConsultantLabel
                          )}
                        </DetailItem>
                        <DetailItem label="Role">
                          {editingProfile ? (
                            <Select
                              value={selectedRole}
                              onOpenChange={(open) => {
                                if (open) fetchRoleOptions()
                              }}
                              onValueChange={(val) => {
                                setSelectedRole(val)
                                setSettingsMessage('')
                                setSettingsErrorMessage('')
                              }}
                              disabled={savingProfile}
                            >
                              <SelectTrigger className="mt-1 bg-white" aria-label="Role">
                                <SelectValue placeholder={roleOptionsLoading ? 'Loading roles...' : 'Choose a role'} />
                              </SelectTrigger>
                              <SelectContent>
                                {selectedRole && !rolesList.some((role) => resolveRoleValue(role) === selectedRole) && (
                                  <SelectItem value={selectedRole}>{selectedRoleLabel}</SelectItem>
                                )}
                                {rolesList.map((role) => (
                                  <SelectItem key={role.id || role.name} value={resolveRoleValue(role)}>
                                    {role.display_name || role.name || 'Role'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            selectedRoleLabel
                          )}
                        </DetailItem>
                      </dl>
                    </SettingsSection>
                  </div>
                </Card>

                {/* Sign-in and access */}
                <Card>
                  <div className="border-b border-slate-200 px-5 py-4">
                    <h3 className="text-base font-semibold text-slate-900">Sign-in and access</h3>
                  </div>
                  <div className="divide-y divide-slate-200">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900">Password</p>
                        {editingPassword ? (
                          <Input
                            type="text"
                            aria-label="New password"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="mt-2 max-w-sm bg-white"
                            autoFocus
                          />
                        ) : (
                          <p className="text-sm text-slate-500">Set a new password for this client.</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {editingPassword ? (
                          <>
                            <Button variant="outline" size="sm" onClick={() => { setEditingPassword(false); setNewPassword('') }} disabled={savingPassword}>
                              Cancel
                            </Button>
                            <Button size="sm" onClick={handleUpdatePassword} disabled={savingPassword || !newPassword.trim()} aria-busy={savingPassword}>
                              {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                              Save password
                            </Button>
                          </>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => setEditingPassword(true)}>
                            Reset password
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900">View as client</p>
                        <p className="text-sm text-slate-500">See the app the way this client sees it.</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleActAsUser} disabled={startingImpersonation} aria-busy={startingImpersonation}>
                        {startingImpersonation ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                        {startingImpersonation ? 'Starting…' : 'View as client'}
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Delete: opens the existing confirmation dialog. */}
                <Card className="border-red-200">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900">Delete client</p>
                      <p className="text-sm text-slate-500">Removes this client&apos;s account. You&apos;ll be asked to confirm.</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={handleDeleteAccount} disabled={deletingAccount}>
                      {deletingAccount ? 'Deleting…' : 'Delete client'}
                    </Button>
                  </div>
                </Card>
              </div>
            </motion.div>
          )}

          {activeDetailTab === 'inbody' && detailViewData && (
            <motion.div
              key="inbody"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="space-y-4"
            >
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold tracking-[-0.01em] text-slate-900">InBody Studio</h2>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {inbodySelectedDate ? `Latest scan: ${formatInbodyDateTime(inbodySelectedDate)}` : 'No scans found yet'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchInbodyData} disabled={inbodyLoading}>
                  {inbodyLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Refresh
                </Button>
              </div>

              {!!inbodyDates.length && (
                <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Scan dates">
                  {inbodyDates.slice(0, 12).map((date) => {
                    const selected = date === inbodySelectedDate
                    return (
                      <button
                        key={date}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => handleInbodyDateSelect(date)}
                        className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                          selected ? 'border-primary bg-accent text-blue-800' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {formatInbodyDateTime(date)}
                      </button>
                    )
                  })}
                </div>
              )}

              {inbodyLoading && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading InBody measurements…</span>
                </div>
              )}
              {!inbodyLoading && inbodyError && (
                <div className="rounded-[10px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{inbodyError}</div>
              )}
              {!inbodyLoading && !inbodyError && !inbodyMeasurement && (
                <div className="rounded-[14px] border border-dashed border-slate-200 px-4 py-10 text-center">
                  <p className="font-medium text-slate-700">User does not have InBody measurements yet.</p>
                  <p className="mt-1 text-xs text-slate-500">As soon as a scan is synced, its measurements will appear here.</p>
                </div>
              )}
              {!inbodyLoading && !inbodyError && !!inbodyMeasurement && (
                <>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                    {[
                      { label: 'Weight', value: inbodyMeasurement.WT, unit: 'lbs' },
                      { label: 'SMM', value: inbodyMeasurement.SMM, unit: 'lbs' },
                      { label: 'Body fat mass', value: inbodyMeasurement.BFM, unit: 'lbs' },
                      { label: 'BMI', value: inbodyMeasurement.BMI, unit: 'kg/m2' },
                      { label: 'PBF', value: inbodyMeasurement.PBF, unit: '%' },
                      { label: 'ECW/TBW', value: inbodyMeasurement.WED, unit: '' },
                    ].map((metric) => (
                      <Card key={metric.label} className="p-4">
                        <p className="text-xs font-semibold text-slate-500">{metric.label}</p>
                        <p className="mt-1 text-xl font-semibold tracking-[-0.01em] text-slate-900">
                          {formatNumberSmart(metric.value)}
                          {metric.unit ? <span className="ml-1 text-xs font-normal text-slate-500">{metric.unit}</span> : null}
                        </p>
                      </Card>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <Card className="overflow-hidden">
                      <div className="border-b border-slate-200 px-4 py-3"><h3 className="text-[15px] font-semibold text-slate-900">Body composition analysis</h3></div>
                      <ul>
                        {[
                          ['Intercellular water', inbodyMeasurement.ICW, 'lbs'],
                          ['Extracellular water', inbodyMeasurement.ECW, 'lbs'],
                          ['Dry lean mass', inbodyMeasurement.DM, 'lbs'],
                          ['Body fat mass', inbodyMeasurement.BFM, 'lbs'],
                        ].map(([label, value, unit]) => (
                          <li key={label} className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 text-sm last:border-b-0">
                            <span className="text-slate-700">{label}</span>
                            <span><span className="font-semibold text-slate-900">{formatNumberSmart(value)}</span> <span className="text-xs text-slate-500">{unit}</span></span>
                          </li>
                        ))}
                      </ul>
                      <dl className="grid grid-cols-1 border-t border-slate-200 sm:grid-cols-3">
                        {[
                          ['Total body water', inbodyMeasurement.TBW, 'lbs'],
                          ['Lean body mass', inbodyMeasurement.FFM, 'lbs'],
                          ['Weight', inbodyMeasurement.WT, 'lbs'],
                        ].map(([label, value, unit]) => (
                          <div key={label} className="border-slate-200 px-4 py-3 sm:border-l sm:first:border-l-0">
                            <dt className="text-xs text-slate-500">{label}</dt>
                            <dd className="mt-0.5 text-lg font-semibold text-slate-900">{formatNumberSmart(value)} <span className="text-xs font-normal text-slate-500">{unit}</span></dd>
                          </div>
                        ))}
                      </dl>
                    </Card>

                    <Card className="overflow-hidden">
                      <div className="border-b border-slate-200 px-4 py-3"><h3 className="text-[15px] font-semibold text-slate-900">Segmental lean analysis</h3></div>
                      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                        {[
                          ['Right arm', inbodyMeasurement.LRA],
                          ['Left arm', inbodyMeasurement.LLA],
                          ['Trunk', inbodyMeasurement.LT],
                          ['Left leg', inbodyMeasurement.LLL],
                          ['Right leg', inbodyMeasurement.LRL],
                        ].map(([label, value]) => (
                          <div key={label} className="rounded-[10px] border border-slate-200 px-3 py-2">
                            <p className="text-xs text-slate-500">{label}</p>
                            <p className="mt-0.5 text-lg font-semibold text-slate-900">{formatNumberSmart(value)} <span className="text-sm font-normal text-slate-500">lbs</span></p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    {[
                      {
                        title: 'Muscle-fat analysis',
                        rows: [
                          ['Weight', Number(inbodyMeasurement.WT) || 0, 'lbs', clampPercent(((Number(inbodyMeasurement.WT) || 0) / 320) * 100)],
                          ['SMM (selected muscle mass)', Number(inbodyMeasurement.SMM) || 0, 'lbs', clampPercent(((Number(inbodyMeasurement.SMM) || 0) / 120) * 100)],
                          ['Body fat mass', Number(inbodyMeasurement.BFM) || 0, 'lbs', clampPercent(((Number(inbodyMeasurement.BFM) || 0) / 120) * 100)],
                        ],
                      },
                      {
                        title: 'Obesity analysis',
                        rows: [
                          ['BMI (body mass index)', Number(inbodyMeasurement.BMI) || 0, 'kg/m2', clampPercent(((Number(inbodyMeasurement.BMI) || 0) / 45) * 100)],
                          ['PBF (percent body fat)', Number(inbodyMeasurement.PBF) || 0, '%', clampPercent(((Number(inbodyMeasurement.PBF) || 0) / 60) * 100)],
                        ],
                      },
                      {
                        title: 'ECW/TBW analysis',
                        note: 'Hydration balance indicator from extracellular vs total body water.',
                        rows: [['ECW/TBW', Number(inbodyMeasurement.WED) || 0, '', clampPercent((((Number(inbodyMeasurement.WED) || 0) - 0.3) / 0.2) * 100)]],
                      },
                    ].map((group) => (
                      <Card key={group.title} className="overflow-hidden">
                        <div className="border-b border-slate-200 px-4 py-3"><h3 className="text-[15px] font-semibold text-slate-900">{group.title}</h3></div>
                        <div className="space-y-3 p-4">
                          {group.rows.map(([label, value, unit, pct]) => (
                            <div key={label} className="space-y-1.5">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-700">{label}</span>
                                <span className="font-semibold text-slate-900">{formatNumberSmart(value)} {unit}</span>
                              </div>
                              <Progress value={pct} aria-label={label} />
                            </div>
                          ))}
                          {group.note && <p className="text-[11px] text-slate-500">{group.note}</p>}
                        </div>
                      </Card>
                    ))}
                  </div>

                  <Card className="overflow-hidden">
                    <div className="border-b border-slate-200 px-4 py-3"><h3 className="text-[15px] font-semibold text-slate-900">Body composition history</h3></div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 md:grid-cols-3 lg:grid-cols-5">
                      {[
                        ['Weight', inbodyMeasurement.WT, 'lbs'],
                        ['SMM / selected muscle mass', inbodyMeasurement.SMM, 'lbs'],
                        ['PBF / percent body fat', inbodyMeasurement.PBF, '%'],
                        ['ECW/TBW', inbodyMeasurement.WED, ''],
                        ['Total body water', inbodyMeasurement.TBW, 'lbs'],
                      ].map(([label, value, unit]) => (
                        <div key={label}>
                          <dt className="text-xs text-slate-500">{label}</dt>
                          <dd className="mt-0.5 text-lg font-semibold text-slate-900">
                            {formatNumberSmart(value)}
                            {unit ? <span className="ml-1 text-xs font-normal text-slate-500">{unit}</span> : null}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <p className="border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
                      Snapshot date: {inbodySelectedDate ? formatInbodyDateTime(inbodySelectedDate) : 'Not available'}
                    </p>
                  </Card>

                  <Card className="overflow-hidden">
                    <div className="border-b border-slate-200 px-4 py-3"><h3 className="text-[15px] font-semibold text-slate-900">Profile highlights</h3></div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 md:grid-cols-3">
                      {[
                        ['Name', inbodyMeasurement.NAME || detailViewData?.name || '-'],
                        ['Height', inbodyMeasurement.HT || '-'],
                        ['Age', inbodyMeasurement.AGE || '-'],
                        ['Gender', inbodyMeasurement.GENDER || '-'],
                        ['BMR', inbodyMeasurement.BMR || '-'],
                        ['Visceral fat level', inbodyMeasurement.VFALevel || '-'],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-xs text-slate-500">{label}</dt>
                          <dd className="mt-0.5 text-sm font-medium text-slate-900">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </Card>

                  {!!inbodyDates.length && (
                    <p className="text-xs text-slate-500">Showing 1 of {inbodyDates.length} available scan{inbodyDates.length > 1 ? 's' : ''}.</p>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Dialog open={deleteModalOpen} onOpenChange={(open) => { if (!deletingAccount) setDeleteModalOpen(open) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete client account?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">This action permanently removes the client and cannot be undone.</p>
          {settingsErrorMessage && <p className="text-sm text-red-600">{settingsErrorMessage}</p>}
          <DialogFooter className="space-x-2">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deletingAccount}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteAccount} disabled={deletingAccount}>
              {deletingAccount && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewProgramWizard
        open={programWizardOpen}
        onOpenChange={setProgramWizardOpen}
        clientId={clientId}
        onFinished={() => {
          setProgramWizardOpen(false)
          setReloadKey((k) => k + 1)
        }}
      />
    </div>
  )
}

export default ConsultantClientDetail
